export const T = {
  ink: "#15201D",
  pine: "#0E5448",
  pineDark: "#0A3D34",
  paper: "#FAF8F3",
  card: "#FFFFFF",
  line: "#E3DFD5",
  mute: "#6B7470",
  immediate: "#B3402A",
  h24: "#C28412",
  h72: "#3D6FA3",
  routine: "#6B7470",
  ok: "#2E7D54",
  clinical: "#0E5448",
  behavioral: "#6B4E9C",
  social: "#A05A21",
  mono: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  sans: "'IBM Plex Sans', system-ui, -apple-system, sans-serif",
};

export const URGENCY = {
  immediate: { label: "Immediate", color: T.immediate, rank: 0 },
  moderate: { label: "Moderate", color: T.h24, rank: 1 },
  actnow: { label: "Act now", color: T.immediate, rank: 0 },
  within24h: { label: "Within 24h", color: T.h24, rank: 1 },
  within72h: { label: "Within 72h", color: T.h72, rank: 2 },
  routine: { label: "Routine", color: T.routine, rank: 3 },
  none: { label: "No action", color: T.routine, rank: 4 },
};

// ---------- Simulated ECM roster ----------
export const ROSTER = {
  MRN001234: { tier: "High acuity", careManager: "J. Okafor", conditions: ["CHF", "T2DM"] },
  MRN005678: { tier: "Moderate", careManager: "S. Tran", conditions: ["OUD", "Depression"] },
  MRN009012: { tier: "High acuity", careManager: "J. Okafor", conditions: ["COPD", "Housing instability"] },
};

// ---------- Sample inbound files ----------
export const SAMPLES = [
  {
    name: "ADT_A03_inpatient_discharge.hl7",
    body: `MSH|^~\\&|EPIC|SKAGIT_VALLEY|RHAPSODY|ECM|20260610083000||ADT^A03|MSG00001|P|2.5.1
EVN|A03|20260610082500
PID|1||MRN001234^^^SVH^MR||GARCIA^MARIA^L||19580412|F|||742 EVERGREEN WAY^^ANACORTES^WA^98221
PV1|1|I|3W^301^A^SKAGIT VALLEY HOSPITAL||||1234^CHEN^DAVID^MD|||MED||||7|||1234^CHEN^DAVID^MD|IP|V0001|||||||||||||||||||01|||||20260605120000|20260610082500
DG1|1||I50.23^Acute on chronic systolic heart failure^I10
DG1|2||E11.65^Type 2 diabetes with hyperglycemia^I10
DG1|3||Z59.0^Homelessness^I10`,
  },
  {
    name: "ADT_A03_ed_discharge.hl7",
    body: `MSH|^~\\&|EPIC|ISLAND_ED|RHAPSODY|ECM|20260610061500||ADT^A03|MSG00002|P|2.5.1
EVN|A03|20260610061000
PID|1||MRN005678^^^IH^MR||REED^MARCUS^T||19910227|M|||1180 COMMERCIAL AVE^^ANACORTES^WA^98221
PV1|1|E|ED^04^B^ISLAND HOSPITAL ED||||5678^PATEL^ANITA^MD|||EME||||1|||5678^PATEL^ANITA^MD|ER|V0002|||||||||||||||||||07|||||20260610023000|20260610061000
DG1|1||F11.23^Opioid dependence with withdrawal^I10
DG1|2||F32.9^Major depressive disorder, single episode^I10
DG1|3||T50.901A^Poisoning by unspecified drug, initial^I10`,
  },
  {
    name: "CCDA_discharge_summary.xml",
    body: `<?xml version="1.0"?>
<ClinicalDocument xmlns="urn:hl7-org:v3">
  <effectiveTime value="20260609174500"/>
  <recordTarget><patientRole>
    <id extension="MRN009012" root="2.16.840.1.113883.19.5"/>
    <patient><name><given>ELENA</given><family>VOSS</family></name>
    <birthTime value="19490803"/></patient>
  </patientRole></recordTarget>
  <componentOf><encompassingEncounter><location><healthCareFacility>
    <name>SKAGIT VALLEY HOSPITAL</name>
  </healthCareFacility></location></encompassingEncounter></componentOf>
  <component><structuredBody>
    <component><section>
      <code code="11535-2" displayName="Discharge Diagnosis"/>
      <text>COPD exacerbation, second admission in 60 days. New diagnosis: moderate malnutrition.</text>
    </section></component>
    <component><section>
      <code code="8648-8" displayName="Hospital Course"/>
      <text>78F admitted with severe dyspnea. Treated with steroids and nebulizers. Patient reports she ran out of her inhaler two weeks ago and could not afford the refill. Lives alone; daughter recently moved out of state. Appears withdrawn, tearful when discussing going home. Screened positive on PHQ-2.</text>
    </section></component>
    <component><section>
      <code code="18776-5" displayName="Discharge Plan"/>
      <text>Discharged home with new prednisone taper and tiotropium. Pulmonology follow-up NOT yet scheduled - office closed at time of discharge. Patient states she has no transportation to appointments. Home O2 ordered, delivery pending.</text>
    </section></component>
    <component><section>
      <code code="29762-2" displayName="Social History"/>
      <text>Lives alone in rural home. Fixed income, reports choosing between medications and groceries some months. No reliable transportation. Limited social support since daughter relocated.</text>
    </section></component>
  </structuredBody></component>
</ClinicalDocument>`,
  },
];

export const ACSC = ["I50", "J44", "E10", "E11", "F1", "F2", "F3", "Z59", "T40", "T50"];

// ---------- AI triage ----------
export const SYSTEM_PROMPT = `You are a triage assistant for a California Medi-Cal Enhanced Care Management (ECM) program. A care manager will review everything you produce — you recommend, a human decides.

Analyze the encounter across THREE domains:
1. CLINICAL: diagnoses, high-risk medication changes, readmission risk, missing follow-up
2. BEHAVIORAL: mental health, substance use, withdrawal, depression/anxiety signals, isolation
3. SOCIAL (SDOH): housing, food, transportation, finances, social support, access barriers

Respond with ONLY valid JSON, no markdown fences:
{
  "urgency": "immediate" | "within_24h" | "within_72h" | "routine" | "none",
  "primary_reason": "one sentence — the single most important reason to act",
  "clinical": ["short specific findings"],
  "behavioral": ["short specific findings"],
  "social": ["short specific findings"],
  "recommended_outreach": "concrete first action for the care manager",
  "talking_points": ["2-4 short patient-facing talking points"],
  "confidence": 0.0-1.0
}
Empty arrays are fine. Be conservative: when in doubt, escalate. Never invent facts not present in the input.`;

export const SLA_MINUTES = { immediate: 15, within_24h: 1440, within_72h: 4320, routine: 10080, none: Infinity };